export const externalLoanTabs = [
  { key: 'application', label: 'Application' },
  { key: 'approvals', label: 'Approvals' },
  { key: 'contract', label: 'Contract' },
  { key: 'bellicks-review', label: 'Bellicks Review' },
  { key: 'repayment', label: 'Repayment' },
  { key: 'timeline', label: 'Timeline' },
];

export const externalLoanApprovalRoles = ['Chairperson', 'Secretary', 'Accountant'];

const stateConfigByApprovalRole = {
  Chairperson: { stateKey: 'awaiting-chairperson', currentStage: 'Chairperson Approval Pending' },
  Secretary: { stateKey: 'awaiting-secretary', currentStage: 'Secretary Approval Pending' },
  Accountant: { stateKey: 'awaiting-accountant', currentStage: 'Accountant Approval Pending' },
};

const stateTabMap = {
  eligible: ['contract', 'timeline'],
  draft: ['application', 'contract', 'timeline'],
  'validation-failed': ['application', 'contract', 'timeline'],
  submitted: ['application', 'approvals', 'contract', 'timeline'],
  'awaiting-chairperson': ['approvals', 'contract', 'timeline'],
  'awaiting-secretary': ['approvals', 'contract', 'timeline'],
  'awaiting-accountant': ['approvals', 'contract', 'timeline'],
  'provider-sync-pending': ['approvals', 'contract', 'timeline'],
  'provider-sync-failed': ['approvals', 'contract', 'timeline'],
  'bellicks-submitted': ['contract', 'bellicks-review', 'timeline'],
  'gm-review': ['contract', 'bellicks-review', 'timeline'],
  approved: ['contract', 'bellicks-review', 'timeline'],
  rejected: ['contract', 'bellicks-review', 'timeline'],
  disbursed: ['contract', 'bellicks-review', 'repayment', 'timeline'],
  'partially-repaid': ['contract', 'bellicks-review', 'repayment', 'timeline'],
  'fully-repaid': ['contract', 'bellicks-review', 'repayment', 'timeline'],
  overdue: ['contract', 'bellicks-review', 'repayment', 'timeline'],
  defaulted: ['contract', 'bellicks-review', 'repayment', 'timeline'],
  cancelled: ['contract', 'timeline'],
};

export const getDefaultTabForState = (stateKey) => {
  const state = stateTabMap[stateKey] || stateTabMap.eligible;
  if (state.includes('application')) return 'application';
  if (state.includes('approvals')) return 'approvals';
  if (state.includes('contract')) return 'contract';
  if (state.includes('bellicks-review')) return 'bellicks-review';
  if (state.includes('repayment')) return 'repayment';
  return 'timeline';
};

export const getAvailableTabsForState = (stateKey) => {
  const allowed = stateTabMap[stateKey] || stateTabMap.eligible;
  return externalLoanTabs.map((tab) => ({
    ...tab,
    disabled: !allowed.includes(tab.key),
  }));
};

const currencyFmt = (value) => new Intl.NumberFormat('en-TZ', { maximumFractionDigits: 0 }).format(Number(value || 0));

const primaryActionMap = {
  eligible: { label: 'Apply Now', targetTab: 'application' },
  draft: { label: 'Continue Application', targetTab: 'application' },
  'validation-failed': { label: 'Continue Application', targetTab: 'application' },
  submitted: { label: 'View Application', targetTab: 'application' },
  'awaiting-chairperson': { label: 'View Approvals', targetTab: 'approvals' },
  'awaiting-secretary': { label: 'View Approvals', targetTab: 'approvals' },
  'awaiting-accountant': { label: 'View Approvals', targetTab: 'approvals' },
  'provider-sync-pending': { label: 'View Timeline', targetTab: 'timeline' },
  'provider-sync-failed': { label: 'View Timeline', targetTab: 'timeline' },
  'bellicks-submitted': { label: 'View Status', targetTab: 'bellicks-review' },
  'gm-review': { label: 'View Status', targetTab: 'bellicks-review' },
  approved: { label: 'View Loan Details', targetTab: 'bellicks-review' },
  rejected: { label: 'View Decision', targetTab: 'bellicks-review' },
  disbursed: { label: 'View Repayment', targetTab: 'repayment' },
  'partially-repaid': { label: 'View Repayment', targetTab: 'repayment' },
  'fully-repaid': { label: 'View Repayment', targetTab: 'repayment' },
  overdue: { label: 'View Repayment', targetTab: 'repayment' },
  defaulted: { label: 'View Repayment', targetTab: 'repayment' },
  cancelled: { label: 'View Application', targetTab: 'timeline' },
};

const approvalProgressMap = {
  'awaiting-chairperson': { completed: 0, pending: 'Chairperson approval is still pending.' },
  'awaiting-secretary': { completed: 1, pending: 'Secretary approval is still pending.' },
  'awaiting-accountant': { completed: 2, pending: 'Accountant approval is still pending.' },
};

export const getPrimaryExternalLoanAction = (stateKey) => primaryActionMap[stateKey] || primaryActionMap.eligible;

export const canStartNewExternalLoanApplication = () => true;

export const getNextActionGuidance = (stateKey, snapshot = {}) => {
  const repayment = snapshot.repayment || {};
  const review = snapshot.bellicksReview || {};
  const disbursement = snapshot.disbursement || {};
  const progress = approvalProgressMap[stateKey];

  switch (stateKey) {
    case 'eligible':
      return 'You are eligible for financing. Start the application when you are ready to request external funding.';
    case 'draft':
      return 'Your application is still a draft. Complete the missing details before submission.';
    case 'validation-failed':
      return 'Some application details need attention before the request can move to review.';
    case 'submitted':
      return 'Your application has been submitted and is waiting for system eligibility review.';
    case 'awaiting-chairperson':
    case 'awaiting-secretary':
    case 'awaiting-accountant':
      return `${progress.completed} of 3 required approvals have been completed. ${progress.pending}`;
    case 'provider-sync-pending':
      return 'All leader approvals are complete. Savvyy is submitting the application to Bellicks Finance automatically.';
    case 'provider-sync-failed':
      return 'All leader approvals remain complete. Provider synchronization needs administrator attention.';
    case 'bellicks-submitted':
      return 'Your application has been submitted to Bellicks Finance. No action is required from you right now.';
    case 'gm-review':
      return `Bellicks Finance is reviewing the application at ${review.stage || 'the current review stage'}.`;
    case 'approved':
      return `Your loan has been approved and is awaiting disbursement. Approved amount: TZS ${currencyFmt(disbursement.approvedAmount)}.`;
    case 'rejected':
      return 'A decision has been made on this application. Review the decision details and any recorded reason.';
    case 'disbursed':
    case 'partially-repaid':
      return `Your next installment is due on ${repayment.nextDueDate || '-'}. Outstanding balance is TZS ${currencyFmt(repayment.outstandingAmount)}.`;
    case 'fully-repaid':
      return 'This external loan is fully repaid. Review the repayment details for the complete payment record.';
    case 'overdue':
      return `This loan is overdue. Overdue amount: TZS ${currencyFmt(repayment.overdueAmount)}.`;
    case 'defaulted':
      return `This loan is in default. Outstanding balance is TZS ${currencyFmt(repayment.outstandingAmount)}.`;
    case 'cancelled':
      return 'This application was cancelled. Use the applications list to switch between lifecycle records.';
    default:
      return 'Review the current lifecycle status before taking the next step.';
  }
};

export const getNextActionMessage = getNextActionGuidance;

export const normalizeExternalLoanRole = (role) => {
  const value = String(role || '').trim().toLowerCase();
  if (['chairperson', 'mwenyekiti', 'chair'].includes(value)) return 'Chairperson';
  if (['secretary', 'katibu'].includes(value)) return 'Secretary';
  if (['accountant', 'muhasibu', 'treasurer'].includes(value)) return 'Accountant';
  return 'Normal Member';
};

export const normalizeExternalLoanGuarantors = (guarantors = []) => externalLoanApprovalRoles.map((role) => {
  const match = guarantors.find((guarantor) => guarantor.role === role) || {};
  const status = match.status || (match.rejected ? 'rejected' : match.approved ? 'approved' : 'pending');
  return {
    ...match,
    role,
    status,
    approved: status === 'approved',
    rejected: status === 'rejected',
  };
});

export const getExternalLoanCurrentRequiredApprover = (application = {}) => {
  const guarantors = normalizeExternalLoanGuarantors(application.guarantors);
  if (application.stateKey === 'rejected' || guarantors.some((guarantor) => guarantor.status === 'rejected')) return null;
  return guarantors.find((guarantor) => guarantor.status !== 'approved') || null;
};

export const getExternalLoanApprovalContext = (application = {}, currentUserRole = 'Normal Member') => {
  const guarantors = normalizeExternalLoanGuarantors(application.guarantors);
  const normalizedRole = normalizeExternalLoanRole(currentUserRole);
  const approvedCount = guarantors.filter((guarantor) => guarantor.status === 'approved').length;
  const rejectedGuarantor = guarantors.find((guarantor) => guarantor.status === 'rejected');
  const currentRequiredApprover = getExternalLoanCurrentRequiredApprover({ ...application, guarantors });
  const activeApproverRole = currentRequiredApprover?.role || null;
  const isAwaitingGuarantors = Boolean(currentRequiredApprover) && ['awaiting-chairperson', 'awaiting-secretary', 'awaiting-accountant'].includes(application.stateKey);
  const alreadyApproved = guarantors.some((guarantor) => guarantor.role === normalizedRole && guarantor.status === 'approved');
  const isApplicantApprover = currentRequiredApprover?.name && currentRequiredApprover.name === application.applicant;
  const canAct = isAwaitingGuarantors
    && normalizedRole === activeApproverRole
    && !alreadyApproved
    && !rejectedGuarantor
    && !isApplicantApprover;

  return {
    guarantors,
    currentUserRole: normalizedRole,
    approvedCount,
    rejectedGuarantor,
    currentRequiredApprover,
    activeApproverRole,
    isAwaitingGuarantors,
    alreadyApproved,
    isApplicantApprover,
    canApprove: canAct,
    canReject: canAct,
    title: canAct ? 'Awaiting Your Approval' : rejectedGuarantor ? 'Application Rejected' : isAwaitingGuarantors ? 'Awaiting Guarantor Approvals' : application.stateConfig?.label || 'Current status',
    message: canAct
      ? `${approvedCount} of 3 approvals completed. Your approval is required as ${normalizedRole}.`
      : isApplicantApprover
        ? 'Self-approval is not enabled for this mock workflow. Another eligible leader must review this application.'
        : rejectedGuarantor
          ? `Rejected by ${rejectedGuarantor.role}. ${application.loanSummary?.rejectionReason || rejectedGuarantor.rejectionReason || ''}`.trim()
          : currentRequiredApprover
            ? `${approvedCount} of 3 approvals completed. Awaiting ${currentRequiredApprover.role} approval.`
            : approvedCount === 3
              ? 'All required guarantor approvals have been completed.'
              : `${approvedCount} of 3 approvals completed.`,
  };
};

const timestamp = () => new Date().toISOString().slice(0, 16).replace('T', ' ');

export const approveExternalLoanApplicationMock = (application = {}, approverRole) => {
  const normalizedRole = normalizeExternalLoanRole(approverRole);
  const now = timestamp();
  const guarantors = normalizeExternalLoanGuarantors(application.guarantors).map((guarantor) => (
    guarantor.role === normalizedRole
      ? { ...guarantor, status: 'approved', approved: true, rejected: false, approvedAt: now }
      : guarantor
  ));
  const nextRequired = getExternalLoanCurrentRequiredApprover({ ...application, guarantors });
  const nextConfig = nextRequired
    ? stateConfigByApprovalRole[nextRequired.role]
    : { stateKey: 'bellicks-submitted', currentStage: 'Submitted to Bellicks' };

  return {
    ...application,
    guarantors,
    stateKey: nextConfig.stateKey,
    status: nextRequired ? 'Awaiting Guarantors' : 'Submitted to Bellicks',
    currentStage: nextConfig.currentStage,
    lastUpdated: now,
    history: [
      ...(application.history || []),
      { date: now, label: `${normalizedRole} approved external loan application`, actor: normalizedRole },
    ],
  };
};

export const rejectExternalLoanApplicationMock = (application = {}, approverRole, reason) => {
  const normalizedRole = normalizeExternalLoanRole(approverRole);
  const now = timestamp();
  const guarantors = normalizeExternalLoanGuarantors(application.guarantors).map((guarantor) => (
    guarantor.role === normalizedRole
      ? { ...guarantor, status: 'rejected', approved: false, rejected: true, rejectedAt: now, rejectionReason: reason }
      : guarantor
  ));

  return {
    ...application,
    guarantors,
    stateKey: 'rejected',
    status: 'Rejected',
    statusGroup: 'rejected',
    currentStage: 'Decision Issued',
    lastUpdated: now,
    loanSummary: {
      ...(application.loanSummary || {}),
      rejectionReason: reason,
    },
    bellicksReview: {
      ...(application.bellicksReview || {}),
      status: 'Rejected',
      stage: `${normalizedRole} rejection`,
      notes: reason,
      rejectionReason: reason,
      decisionDate: now,
    },
    history: [
      ...(application.history || []),
      { date: now, label: `Application rejected by ${normalizedRole}`, actor: normalizedRole },
    ],
  };
};
